//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import { theme as componentTheme } from '@bvengineering/unison-core';
import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { Modal, ModalBody, ModalHeader } from 'unison';

import { InvisibleButton as ModalClose } from '@components/atoms/button/Button';
import { ITrackableComponent } from '@utils/withTracking';

interface IOwnProps extends Partial<ITrackableComponent> {
  headerTitle: string;
  isOpen: boolean;
  onClose: () => void;
  actionButton?: ReactNode;
  className?: string;
  isActive?: boolean;
  wideLayout?: boolean;
}

export const FullscreenModal: React.FunctionComponent<IOwnProps> = (props) => {
  const {
    trackingId,
    isOpen,
    onClose,
    headerTitle,
    actionButton,
    className,
    isActive,
    wideLayout,
  } = props;

  const headerHeight = `calc(${componentTheme.spacings[7]} + ${componentTheme.spacings[2]})`;

  return (
    <Container isOpen={isOpen} className={className} trackingId={trackingId}>
      <Header
        className='cu-bs-2'
        headerHeight={headerHeight}
        isActive={isActive}
      >
        <div className='cu-d-flex cu-align-items-center cu-justify-content-start'>
          <ModalClose iconName='close' onClick={onClose} />
          <ModalTitle isActive={isActive} className='cu-text-subsection'>
            {headerTitle}
          </ModalTitle>
        </div>
        {actionButton && (
          <div className='cu-d-flex cu-justify-content-end'>{actionButton}</div>
        )}
      </Header>
      <Body headerHeight={headerHeight} wideLayout={wideLayout}>
        {props.children}
      </Body>
    </Container>
  );
};

const Container = styled(Modal)`
  && {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background: ${({ theme }) => theme.colors.white};
  }
`;

interface IHeader {
  headerHeight: string;
  isActive?: boolean;
}

const Header = styled(ModalHeader)<IHeader>`
  && {
    display: flex;
    align-items: center;
    padding: 0 ${({ theme }) => theme.spacings['4']};
    height: ${({ headerHeight }) => headerHeight};
    flex-shrink: 0;

    ${({ theme, isActive }) => css`
      background-color: ${isActive
        ? theme.colors.aliases.primary
        : theme.colors.white};
      color: ${isActive ? theme.colors.white : theme.colors.gray9};
    `}
  }
`;

const ModalTitle = styled.span<Pick<IHeader, 'isActive'>>`
  && {
    margin-left: ${({ theme }) => theme.spacings['3']};
    color: ${({ theme, isActive }) =>
      isActive ? theme.colors.white : theme.colors.gray9};
  }
`;

interface IBody {
  wideLayout?: boolean;
}

const Body = styled(ModalBody)<IHeader & IBody>`
  && {
    margin: 0 auto;
    height: ${({ headerHeight }) => `calc(100vh - ${headerHeight})`};
    overflow: auto;

    ${({ wideLayout }) => css`
      width: ${wideLayout ? '100%' : '60%'};
      padding: ${wideLayout ? '0 15%' : ''};
    `}
  }

  @media (max-width: 1200px) {
    && {
      width: 100%;
    }
  }
`;
